// https://minify-js.com/
import { ThemeEvents, CartUpdateEvent } from "@theme/events";
import { $4, $$4, $id4, debounce } from '@theme/utilities';
import { getCookie, setCookie } from '@theme/cookies';
import { getterAdd, getterGet, getterRunFn, btnTooltip, DrawerComponent } from "@theme/global";
import {
  getNewsletterSuppressionMs,
  getPromptCoordinator,
  isFreshState,
  readJsonState,
  writeJsonState
} from '@theme/storefront-prompts';


const {dialogClose, dialogOpen, cartCount} = ThemeEvents;
const CUSTOM_ANALYTICS_EVENT = 'hdt:custom-analytics';

function publish_custom_analytics_event_fn(type, params, useBeacon = false) {
  if (!type) return;
  document.dispatchEvent(new CustomEvent(CUSTOM_ANALYTICS_EVENT, {
    detail: {
      type,
      params: params || {},
      options: { useBeacon }
    }
  }));
}

// WISHLIST
var $wishlist_list = $id4('hdt_wishlist_list'),
  $compare_list = $id4('hdt_compare_list'),
  nameCachedWishlist = 'theme4:wishlist:id',
  nameCachedWishlistVariants = 'theme4:wishlist:variants',
  nameCachedCompare = 'theme4:compare:id';
// if exit $wishlist_list is use app wishlist the4
if ($wishlist_list) {
  var arr_wishlist_list = $wishlist_list.textContent ? $wishlist_list.textContent.split(' ') : [];
}
else {
  var arr_wishlist_list = (!localStorage.getItem(nameCachedWishlist)) ? [] : localStorage.getItem(nameCachedWishlist).split(',');  // remove id: and conver array
}
if ($compare_list){
  var arr_compare_list = $compare_list.textContent ? $compare_list.textContent.split(' ') : [];
}
else {
  var arr_compare_list = (!localStorage.getItem(nameCachedCompare)) ? [] : localStorage.getItem(nameCachedCompare).split(',');  // remove id: and conver array
}
// arr_wishlist_list = [1234, 5678, 9011]
// arr_compare_list = [1234, 5678, 9011]

function parse_json_storage_fn(key, fallback = {}) {
  try {
    return JSON.parse(localStorage.getItem(key) || '') || fallback;
  } catch (error) {
    return fallback;
  }
}

var wishlist_variant_map = parse_json_storage_fn(nameCachedWishlistVariants, {});

function save_wishlist_variant_map_fn() {
  localStorage.setItem(nameCachedWishlistVariants, JSON.stringify(wishlist_variant_map));
}

function normalize_variant_data_fn(data = {}) {
  const variantId = data.variantId || data.variant_id || data.id;
  if (!variantId) return null;
  const normalized = {
    id: `${variantId}`
  };
  if (data.title || data.variantTitle || data.variant_title) normalized.title = data.title || data.variantTitle || data.variant_title;
  if (data.url || data.variantUrl || data.variant_url) normalized.url = data.url || data.variantUrl || data.variant_url;
  if (data.image || data.variantImage || data.variant_image) normalized.image = data.image || data.variantImage || data.variant_image;
  if (data.option1) normalized.option1 = data.option1;
  if (data.option2) normalized.option2 = data.option2;
  if (data.option3) normalized.option3 = data.option3;
  if (Array.isArray(data.options)) normalized.options = data.options;
  return normalized;
}

function normalize_wishlist_variant_store_fn() {
  let didChange = false;
  Object.keys(wishlist_variant_map || {}).forEach((productId) => {
    const value = wishlist_variant_map[productId];
    if (Array.isArray(value)) {
      const entries = value.map(normalize_variant_data_fn).filter(Boolean);
      wishlist_variant_map[productId] = entries;
      didChange = didChange || entries.length !== value.length;
      return;
    }
    const normalized = normalize_variant_data_fn(value);
    wishlist_variant_map[productId] = normalized ? [normalized] : [];
    didChange = true;
  });
  if (didChange) save_wishlist_variant_map_fn();
}

normalize_wishlist_variant_store_fn();

function get_wishlist_variant_entries_fn(productId) {
  if (!productId) return [];
  const value = wishlist_variant_map[`${productId}`];
  if (!Array.isArray(value)) return [];
  return value;
}

function get_wishlist_variant_fn(productId) {
  return get_wishlist_variant_entries_fn(productId)[0] || null;
}

function has_wishlist_product_fn(productId) {
  return arr_wishlist_list.map((id) => `${id}`).indexOf(`${productId}`) > -1;
}

function has_wishlist_variant_fn(productId, variantId) {
  if (!productId || !variantId) return false;
  return get_wishlist_variant_entries_fn(productId).some((entry) => `${entry.id}` === `${variantId}`);
}

function set_wishlist_variant_fn(productId, variantData) {
  const normalized = normalize_variant_data_fn(variantData);
  if (!productId || !normalized) return;
  const productKey = `${productId}`;
  const entries = get_wishlist_variant_entries_fn(productKey);
  const existingIndex = entries.findIndex((entry) => `${entry.id}` === `${normalized.id}`);
  if (existingIndex > -1) {
    entries[existingIndex] = { ...entries[existingIndex], ...normalized };
  } else {
    entries.unshift(normalized);
  }
  wishlist_variant_map[productKey] = entries;
  save_wishlist_variant_map_fn();
}

function remove_wishlist_variant_fn(productId, variantId = null) {
  if (!productId || !wishlist_variant_map[`${productId}`]) return;
  if (variantId) {
    const entries = get_wishlist_variant_entries_fn(productId).filter((entry) => `${entry.id}` !== `${variantId}`);
    if (entries.length) {
      wishlist_variant_map[`${productId}`] = entries;
    } else {
      delete wishlist_variant_map[`${productId}`];
    }
  } else {
    delete wishlist_variant_map[`${productId}`];
  }
  save_wishlist_variant_map_fn();
}

function get_wishlist_entry_count_fn() {
  return arr_wishlist_list.reduce((total, productId) => {
    return total + Math.max(get_wishlist_variant_entries_fn(productId).length, 1);
  }, 0);
}

function update_wishlist_page_meta_count_fn() {
  if (!window.isPageWishlist) return;
  const meta = document.querySelector('.hdt-wishlist-page__meta');
  if (!meta) return;
  meta.textContent = meta.textContent.replace(/^\s*\d+/, get_wishlist_entry_count_fn());
}

function remove_wishlist_card_variant_dom_fn(button) {
  const card = button.closest('hdt-card-product');
  if (card) {
    card.remove();
    return true;
  }
  return false;
}

function get_variant_id_from_product_context_fn(button) {
  const card = button.closest('hdt-card-product');
  const checkedCardVariant = card?.querySelector('.hdt-color-list input[data-vid]:checked');
  if (checkedCardVariant?.dataset.vid) return checkedCardVariant.dataset.vid;
  const scopedRoot = button.closest('form, hdt-product-info, .hdt-product-info, .hdt-main-product, .shopify-section') || document;
  const variantInput = scopedRoot.querySelector('input[name="id"], select[name="id"]');
  return variantInput?.value || null;
}

function get_variant_data_from_wishlist_button_fn(button) {
  if (button.hasAttribute('data-wishlist-product-only')) return null;
  const variantId = button.dataset.variantId || get_variant_id_from_product_context_fn(button);
  return normalize_variant_data_fn({
    id: variantId,
    title: button.dataset.variantTitle,
    url: button.dataset.variantUrl,
    image: button.dataset.variantImage,
    option1: button.dataset.variantOption1,
    option2: button.dataset.variantOption2,
    option3: button.dataset.variantOption3
  });
}

function get_wishlist_button_action_fn(button) {
  const productId = button.dataset.id;
  const variantData = get_variant_data_from_wishlist_button_fn(button);
  if (variantData?.id) {
    return has_wishlist_variant_fn(productId, variantData.id) ? actionAfterWishlistAdded : 'add';
  }
  return has_wishlist_product_fn(productId) ? actionAfterWishlistAdded : 'add';
}

function update_wishlist_button_variant_state_fn(button) {
  if (!button?.isFnWishlist || button.hasAttribute('remove-on-page')) return;
  const nextAction = get_wishlist_button_action_fn(button);
  if (button.action !== nextAction) button.setAttribute('action', nextAction);
}

let wishlistVariantUpdateRaf = 0;
function schedule_wishlist_variant_buttons_update_fn(root = document) {
  if (wishlistVariantUpdateRaf) return;
  wishlistVariantUpdateRaf = requestAnimationFrame(() => {
    wishlistVariantUpdateRaf = 0;
    $$4('hdt-wishlist[data-id]', root).forEach(update_wishlist_button_variant_state_fn);
  });
}

var linkWishlistApp = '/apps/ecomrise/wishlist',
  linkCompareApp = '/apps/ecomrise/compare',
  actionAfterWishlistAdded = themeHDN.extras.AddedWishlistRemove ? 'remove' : 'added',
  actionAfterCompareAdded = themeHDN.extras.AddedCompareRemove ? 'remove' : 'added',
  limitWishlist = $wishlist_list ? 100 : 50,
  limitCompare = 5,
  conver_to_link_fn = function (prefix = this.textFn, array = this.array) {
    const x = themeHDN.routes.search_url + `/?view=${prefix}`,
      y = x + '&type=product&options[unavailable_products]=last&q=';
    return (array.length) ? y + encodeURI(`id:${array.join(' OR id:')}`) : x;
  };
var wishlistToastTimer;
var wishlistToastPositionTimer;
function position_wishlist_toast_fn(toast) {
  const mobile = window.matchMedia('(max-width: 767px)').matches;
  const viewportHeight = window.innerHeight;
  let offset = 16;
  const selectors = mobile
    ? '.hdt-sticky-btn-atc, .hdt-toolbar, #shopify-pc__banner, .shopify-pc__banner'
    : '.hdt-section-header, hdt-sticky-header';
  const obstacles = Array.from(document.querySelectorAll(selectors));
  if (mobile) {
    const consentRoot = document.querySelector('pandectes-cmp')?.shadowRoot;
    if (consentRoot) obstacles.push(...consentRoot.querySelectorAll('dialog.notice'));
  }
  obstacles.forEach((element) => {
    const rect = element.getBoundingClientRect();
    const style = getComputedStyle(element);
    if (!rect.width || !rect.height || style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) return;
    if (mobile && rect.top < viewportHeight && rect.bottom > 0) offset = Math.max(offset, viewportHeight - rect.top + 16);
    if (!mobile && rect.bottom > 0 && rect.top < viewportHeight) offset = Math.max(offset, rect.bottom + 16);
  });
  toast.style.setProperty('--wishlist-toast-offset', `${Math.round(offset)}px`);
}
function hide_wishlist_toast_fn() {
  clearTimeout(wishlistToastTimer);
  clearInterval(wishlistToastPositionTimer);
  const toast = $id4('hdt-wishlist-toast');
  if (!toast) return;
  toast.removeAttribute('active');
  if (typeof toast.hidePopover === 'function' && toast.matches(':popover-open')) toast.hidePopover();
}
function get_wishlist_toast_host_fn() {
  const cartDrawer = $id4('CartDrawer');
  return cartDrawer?.open ? cartDrawer : document.body;
}
function show_wishlist_toast_fn(href) {
  let toast = $id4('hdt-wishlist-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'hdt-wishlist-toast';
    toast.className = 'hdt-wishlist-toast';
    toast.setAttribute('popover', 'manual');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.addEventListener('click', (event) => { if (event.target.closest('a')) hide_wishlist_toast_fn(); });
    toast.innerHTML = '<span class="hdt-wishlist-toast__message"></span><a class="hdt-wishlist-toast__link"></a>';
    document.body.appendChild(toast);
  }
  if (typeof toast.hidePopover === 'function' && toast.matches(':popover-open')) {
    toast.hidePopover();
  }
  const toastHost = get_wishlist_toast_host_fn();
  if (toast.parentElement !== toastHost) toastHost.appendChild(toast);
  toast.querySelector('.hdt-wishlist-toast__message').textContent = themeHDN.extras.text_wis_toast_added || themeHDN.extras.text_wis_added;
  const link = toast.querySelector('.hdt-wishlist-toast__link');
  link.textContent = themeHDN.extras.text_wis_toast_view || themeHDN.extras.text_wis_added;
  link.href = href || conver_to_link_fn('wishlist', arr_wishlist_list);
  toast.removeAttribute('active');
  clearTimeout(wishlistToastTimer);
  clearInterval(wishlistToastPositionTimer);
  position_wishlist_toast_fn(toast);
  if (typeof toast.showPopover === 'function') {
    toast.showPopover();
  }
  requestAnimationFrame(() => {
    toast.setAttribute('active', '');
  });
  // Observe moving sticky bars only during the short-lived notification.
  wishlistToastPositionTimer = setInterval(() => position_wishlist_toast_fn(toast), 200);
  wishlistToastTimer = setTimeout(hide_wishlist_toast_fn, 4000);
}
function update_cart_count_dots_fn() {
  $$4('hdt-cart-count').forEach(count => {
    const countValue = Number((count.textContent || '').trim());
    count.toggleAttribute('hidden', !Number.isFinite(countValue) || countValue <= 0);
  });
}
let cartCountDotsRaf = 0;
function schedule_cart_count_dots_update_fn() {
  if (cartCountDotsRaf) return;
  cartCountDotsRaf = requestAnimationFrame(() => {
    cartCountDotsRaf = 0;
    update_cart_count_dots_fn();
  });
}
update_cart_count_dots_fn();
document.addEventListener(cartCount, (event) => {
  if (typeof event.detail?.item_count === 'number') {
    $$4('hdt-cart-count').forEach(count => {
      count.textContent = event.detail.item_count;
    });
  }
  schedule_cart_count_dots_update_fn();
});
// Reset if limit change
if (arr_wishlist_list.length > limitWishlist) {
  arr_wishlist_list.splice(limitWishlist - 1, arr_wishlist_list.length - 1);
  localStorage.setItem(nameCachedWishlist, arr_wishlist_list.toString());
}
// Check is page has item but not show reload page
if (window.isPageWishlist) {
  if (arr_wishlist_list.length && !window.isWishlistPerformed) {
    window.location.href = conver_to_link_fn('wishlist', arr_wishlist_list)
  } else {
    window.history.replaceState({}, document.title, conver_to_link_fn('wishlist', []));
  }
  themeHDN.wisHref = conver_to_link_fn('wishlist', arr_wishlist_list);
}

// Reset if limit change
if (arr_compare_list.length > limitCompare) {
  arr_compare_list.splice(limitCompare - 1, arr_compare_list.length - 1);
  localStorage.setItem(nameCachedCompare, arr_compare_list.toString());
}
// Check is page has item but not show reload page
if (window.isPageCompare) {
  if (arr_compare_list.length && !window.isComparePerformed) {
    window.location.href = conver_to_link_fn('compare', arr_compare_list)
  } else {
    window.history.replaceState({}, document.title, conver_to_link_fn('compare', []));
  }
}
var _wishlist_id, _wishlist_handle,
  _update_wis_text, update_wis_text_fn,
  _update_wis_btns, update_wis_btns_fn,
  _click_wis, click_wis_fn,
  _add_wis, add_wis_fn,
  _remove_wis, remove_wis_fn,
  _action_after_remove_add, action_after_remove_add_fn,
  _show_popup_compare, show_popup_compare_fn,
  _conver_to_link;
class Wishlist extends btnTooltip {
  constructor() {
    super();
    getterAdd(_wishlist_id, this, this.dataset.id);
    getterAdd(_wishlist_handle, this, this.dataset.handle);
    getterAdd(_update_wis_text, this);
    getterAdd(_update_wis_btns, this);
    getterAdd(_click_wis, this);
    getterAdd(_add_wis, this);
    getterAdd(_remove_wis, this);
    getterAdd(_action_after_remove_add, this);
    getterAdd(_show_popup_compare, this);
    getterAdd(_conver_to_link, this);
    this.tabIndex = 0;
    this.addEventListener("click", (event) => getterRunFn(_click_wis, this, click_wis_fn).call(this, event));
    this.addEventListener("keydown", (event) => {
      if (event.key == "Enter") getterRunFn(_click_wis, this, click_wis_fn).call(this, event)
    });
  }
  static get observedAttributes() {
    return ["action"];
  }
  get isFnWishlist() {
    return true;
  }
  get textFn() {
    return 'wishlist';
  }
  get isPageWishlistorCompare() {
    if (this.closest('gh-wishlist-drawer')) return false;
    //add code check page
    return this.isFnWishlist ? window.isPageWishlist : window.isPageCompare;
  }
  get limit() {
    return this.isFnWishlist ? limitWishlist : limitCompare;
  }
  get nameCached() {
    return this.isFnWishlist ? nameCachedWishlist : nameCachedCompare;
  }
  get array() {
    return this.isFnWishlist ? arr_wishlist_list : arr_compare_list;
  }
  get actionAfterAdded() {
    return this.isFnWishlist ? actionAfterWishlistAdded : actionAfterCompareAdded;
  }
  get action() {
    return this.getAttribute('action');
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || (oldValue === null && newValue === 'add')) return;
    getterRunFn(_update_wis_text, this, update_wis_text_fn).call(this, newValue);
    super.attributeChangedCallback(name, oldValue, newValue);
  }
  connectedCallback() {
    super.connectedCallback();
    if (this.isFnWishlist) {
      update_wishlist_button_variant_state_fn(this);
      return;
    }
    if (this.array.indexOf(getterGet(_wishlist_id, this)) > -1) {
      this.setAttribute('action', this.actionAfterAdded);
    }
  }
};
_wishlist_id = new WeakMap();
_wishlist_handle = new WeakMap();
_update_wis_text = new WeakSet();
_update_wis_btns = new WeakSet();
_click_wis = new WeakSet();
_add_wis = new WeakSet();
_remove_wis = new WeakSet();
_action_after_remove_add = new WeakSet();
_conver_to_link = new WeakSet();
_show_popup_compare = new WeakSet();
update_wis_text_fn = function (value) {
  if (this.hasAttribute('remove-on-page')) return;
  const text = themeHDN.extras[`text_${this.isFnWishlist ? 'wis' : 'cp'}_${value}`],
    icon = themeHDN.extras[`icon_${this.isFnWishlist ? 'wis' : 'cp'}_${value}`];
  if (icon) this.firstElementChild.replaceWith(document.createRange().createContextualFragment(icon));
  if (text) {
    this.lastElementChild.textContent = text;
    this.setAttribute('data-txt-tt',text)
  }
};
update_wis_btns_fn = function (_action) {
  // Update Button
  if (this.isFnWishlist) {
    $$4(`hdt-wishlist[data-id="${getterGet(_wishlist_id, this)}"]`).forEach(update_wishlist_button_variant_state_fn);
  } else {
    $$4(`hdt-${this.textFn}[data-id="${getterGet(_wishlist_id, this)}"]`).forEach(btn => {
      btn.setAttribute('action', _action);
    });
  }
  // Update Count and link
  document.dispatchEvent(new CustomEvent(`theme4:${this.textFn}:update`, {
    bubbles: true,
    detail: 'the4'  // arr_wishlist_list
  }));
  if ((window.isPageWishlist)) themeHDN.wisHref = conver_to_link_fn('wishlist', arr_wishlist_list);
};
click_wis_fn = function (e) {
  if (this.isFnWishlist && this.action !== 'add') {
    const productId = getterGet(_wishlist_id, this);
    const wishlistVariantData = get_variant_data_from_wishlist_button_fn(this);
    if (wishlistVariantData?.id && !has_wishlist_variant_fn(productId, wishlistVariantData.id)) {
      getterRunFn(_add_wis, this, add_wis_fn).call(this);
      return;
    }
  }

  if (this.action == 'add') {
    // ADD
    getterRunFn(_add_wis, this, add_wis_fn).call(this);
  } else if (this.action === 'added') {
    // Saved wishlist entries open the shared side panel when available.
    if (this.isFnWishlist && document.querySelector('gh-wishlist-drawer')) {
      document.dispatchEvent(new CustomEvent('gh:wishlist:open', { detail: { opener: this } }));
    } else if (!this.isFnWishlist && themeHDN.extras.enableComparePopup) {
      getterRunFn(_show_popup_compare, this, show_popup_compare_fn).call(this);
    } else {
      window.location.href = getterRunFn(_conver_to_link, this, conver_to_link_fn).call(this);
    }
  } else {
    // REMOVE
    getterRunFn(_remove_wis, this, remove_wis_fn).call(this, e);
  }
};
add_wis_fn = function (e) {
  const wishlistVariantData = this.isFnWishlist ? get_variant_data_from_wishlist_button_fn(this) : null;
  const productId = getterGet(_wishlist_id, this);
  if (this.isFnWishlist && wishlistVariantData?.id && has_wishlist_product_fn(productId)) {
    set_wishlist_variant_fn(productId, wishlistVariantData);
    getterRunFn(_action_after_remove_add, this, action_after_remove_add_fn).call(this, this.actionAfterAdded);
    show_wishlist_toast_fn(getterRunFn(_conver_to_link, this, conver_to_link_fn).call(this));
    return;
  }
  if ($wishlist_list && this.isFnWishlist || $compare_list && !this.isFnWishlist) {
    // app wishlist
    this.setAttribute('aria-busy', true);
    fetch(this.isFnWishlist ? linkWishlistApp : linkCompareApp, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        product_id: getterGet(_wishlist_id, this),
        product_handle: getterGet(_wishlist_handle, this),
        action: 'add'
      })
    })
    .then((response) => {
      return response.json()
    })
    .then((data) => {
      if (data.status != 'success') {
        console.error(data.message || 'Unknow error');
        return;
      }
      if(this.isFnWishlist) {
        arr_wishlist_list = JSON.parse(data.response.metafield.value).ecomrise_ids;
        if (!Array.isArray(arr_wishlist_list)) {
          arr_wishlist_list = arr_wishlist_list.split(',');
        }
      }
      if (!this.isFnWishlist) {
        arr_compare_list = JSON.parse(data.response.metafield.value).ecomrise_ids;
        if (!Array.isArray(arr_compare_list)) {
          arr_compare_list = arr_compare_list.split(',');
        }
        getterRunFn(_show_popup_compare, this, show_popup_compare_fn).call(this);
      }
      if (this.isFnWishlist) set_wishlist_variant_fn(getterGet(_wishlist_id, this), wishlistVariantData);
      getterRunFn(_action_after_remove_add, this, action_after_remove_add_fn).call(this, this.actionAfterAdded);
      if (this.isFnWishlist) show_wishlist_toast_fn(getterRunFn(_conver_to_link, this, conver_to_link_fn).call(this));
    })
    .catch(function (error) {
      console.log('Error: ' + error);
    })
    .finally(() => {
      this.setAttribute('aria-busy', false);
    });
  }
  else {
    // local wishlist
    // adds the specified elements to the beginning of an array
    if (!this.isFnWishlist || !has_wishlist_product_fn(getterGet(_wishlist_id, this))) {
      this.array.unshift(getterGet(_wishlist_id, this));
    }
    //console.log(this.array, getterGet(_wishlist_id, this))
    if (this.array.length > this.limit) {
      // if over limit remove element on last array
      let arraySplice = this.array.splice(this.limit, 1);
      if (this.isFnWishlist) remove_wishlist_variant_fn(arraySplice[0]);
      // arraySplice: allway has 1 element
      $$4(`hdt-${this.textFn}[data-id="${arraySplice[0]}"]`).forEach(btn => {
        btn.setAttribute('action', 'add');
      });
    }
    if (!this.isFnWishlist) getterRunFn(_show_popup_compare, this, show_popup_compare_fn).call(this);
    if (this.isFnWishlist) set_wishlist_variant_fn(getterGet(_wishlist_id, this), wishlistVariantData);
    getterRunFn(_action_after_remove_add, this, action_after_remove_add_fn).call(this, this.actionAfterAdded);
    localStorage.setItem(this.nameCached, this.array.toString());
    if (this.isFnWishlist) show_wishlist_toast_fn(getterRunFn(_conver_to_link, this, conver_to_link_fn).call(this));
  }
};
remove_wis_fn = function (e) {
  e.preventDefault();
  e.stopPropagation();
  const productId = getterGet(_wishlist_id, this);
  const wishlistVariantData = this.isFnWishlist ? get_variant_data_from_wishlist_button_fn(this) : null;
  const removalContext = this.closest('gh-wishlist-drawer') ? 'drawer' : window.isPageWishlist ? 'wishlist_page' : 'storefront';
  const trackRemoval = () => { if (this.isFnWishlist) publish_custom_analytics_event_fn('wishlist_remove', { interaction_source: removalContext, item_count: 1 }, true); };
  const canRemoveSingleVariant = this.isFnWishlist && wishlistVariantData?.id && has_wishlist_variant_fn(productId, wishlistVariantData.id);

  if (canRemoveSingleVariant) {
    remove_wishlist_variant_fn(productId, wishlistVariantData.id);
    const remainingVariants = get_wishlist_variant_entries_fn(productId);
    if (remainingVariants.length) {
      if (this.hasAttribute('remove-on-page')) {
        remove_wishlist_card_variant_dom_fn(this);
      } else {
        this.setAttribute('action', 'add');
      }
      document.dispatchEvent(new CustomEvent('theme4:wishlist:update', {
        bubbles: true,
        detail: 'the4'
      }));
      trackRemoval();
      return;
    }
  }

  // REMOVE
  if ($wishlist_list && this.isFnWishlist || $compare_list && !this.isFnWishlist) {
    // app wishlist
    this.setAttribute('aria-busy', true);
    fetch(this.isFnWishlist ? linkWishlistApp : linkCompareApp, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        product_id: getterGet(_wishlist_id, this),
        product_handle: getterGet(_wishlist_handle, this),
        action: 'add',
        _method: 'DELETE'
      })
    })
    .then((response) => {
      return response.json()
    })
    .then((data) => {
      if (data.status != 'success') {
        console.error(data.message || 'Unknow error');
        return;
      }
      arr_wishlist_list = JSON.parse(data.response.metafield.value).ecomrise_ids;
      if (!Array.isArray(arr_wishlist_list)) {
        arr_wishlist_list = arr_wishlist_list.split(',');
      }
      if (this.isFnWishlist) remove_wishlist_variant_fn(getterGet(_wishlist_id, this));
      getterRunFn(_action_after_remove_add, this, action_after_remove_add_fn).call(this, 'add');
      trackRemoval();
    })
    .catch(function (error) {
      console.log('Error: ' + error);
    })
    .finally(() => {
      this.setAttribute('aria-busy', false);
    });
  } else {
    // local wishlist
    this.array.splice(this.array.indexOf(getterGet(_wishlist_id, this)), 1);
    if (this.isFnWishlist) remove_wishlist_variant_fn(getterGet(_wishlist_id, this));
    localStorage.setItem(this.nameCached, this.array.toString());

    getterRunFn(_action_after_remove_add, this, action_after_remove_add_fn).call(this, 'add');
    trackRemoval();
  }
};
action_after_remove_add_fn = function (action) {

  if (this.getAttribute('action') == 'remove' && this.hasAttribute('remove-on-page')) {
    if (this.isFnWishlist) {      
      this.setAttribute('aria-busy', true);
      if (this.isPageWishlistorCompare) window.location.href = getterRunFn(_conver_to_link, this, conver_to_link_fn).call(this);
      // this.closest('.hdt-card-product').remove();
    } else {
      $$4(`.hdt-compare-item-${getterGet(_wishlist_id, this)}`).forEach(item => {
        item.remove();
        // console.log("Remove compare item");
      });
    }
    getterRunFn(_update_wis_btns, this, update_wis_btns_fn).call(this, action);
    if (this.array.length == 0) {
      $id4('drawerCompare')?.closest('hdt-drawer')?.close();
      if (this.isPageWishlistorCompare) window.location.href = getterRunFn(_conver_to_link, this, conver_to_link_fn).call(this);
    }
    //this.setAttribute('action', action);
  } else {
    getterRunFn(_update_wis_btns, this, update_wis_btns_fn).call(this, action);
    // is page wishlist or compare reload page
    if (this.isPageWishlistorCompare) window.location.href = getterRunFn(_conver_to_link, this, conver_to_link_fn).call(this);
  }
};
show_popup_compare_fn = function (event) {
  if (!themeHDN.extras.enableComparePopup || this.isPageWishlistorCompare) return;
  // add code if want show popup compare
  $id4('drawerCompare')?.closest('hdt-drawer')?.open();
  fetch(conver_to_link_fn('compare', arr_compare_list) + "&section_id=compare-offcanvas")
    .then((response) => response.text())
    .then((text) => {
      const html = document.createElement('div');
      html.innerHTML = text;
      const prds = html.querySelector('offcanvas-compare');

      if (prds && prds.innerHTML.trim().length) {

        $4("offcanvas-compare").innerHTML = prds.innerHTML;
      }
    })
    .catch((e) => {
      console.error(e);
    });
};
var _clear_all, clear_all_fn;
class ClearAll extends HTMLElement {
  constructor() {
    super();
    getterAdd(_clear_all, this);
    this.tabIndex = 0;
    this.addEventListener("click", getterRunFn(_clear_all, this, clear_all_fn));
    this.addEventListener("keydown", (event) => {
      if (event.key == "Enter") getterRunFn(_clear_all, this, clear_all_fn).call(this)
    });
  }
  get ofFn() {
    return this.getAttribute('of-fn'); //'compare', 'wishlist'
  }
  get nameCached() {
    return this.ofFn == 'compare' ? nameCachedCompare : nameCachedWishlist;
  }
  set array(value) {
    this.ofFn == 'compare' ? arr_compare_list = value : arr_wishlist_list = value;
  }
};
_clear_all = new WeakSet();
clear_all_fn = function () {
  const removedCount = this.ofFn === 'wishlist' ? get_wishlist_entry_count_fn() : 0;
  localStorage.removeItem(this.nameCached);
  if (this.ofFn == 'wishlist') {
    localStorage.removeItem(nameCachedWishlistVariants);
    wishlist_variant_map = {};
  }
  this.array = [];
  if (removedCount) publish_custom_analytics_event_fn('wishlist_clear', { interaction_source: this.closest('gh-wishlist-drawer') ? 'drawer' : 'wishlist_page', item_count: removedCount }, true);
  // Update Buttons
  $$4(`hdt-${this.ofFn}[action="added"]`).forEach(btn => {
    btn.setAttribute('action', 'add');
  });
  // Update Count and link
  document.dispatchEvent(new CustomEvent(`theme4:${this.ofFn}:update`, {
    bubbles: true,
    detail: 'the4'
  }));
  $id4(`drawerCompare`)?.closest('hdt-drawer')?.close();
  if ($4(`#drawerCompare offcanvas-compare`)){
    $4(`#drawerCompare offcanvas-compare`).innerHTML = "";
  }
  if (this.dataset.redirectUrl) {
    window.location.href = this.dataset.redirectUrl;
  }
};
customElements.define("hdt-clear-all", ClearAll);
// eg: <hdt-clear-all role="button" of-fn="compare">Clear All </hdt-clear-all>

// Update count
class WishlistCount extends HTMLElement {
  constructor() {
    super();
    const updateCount = () => {
      const count = this.isFnWishlist ? get_wishlist_entry_count_fn() : this.array.length;
      this.textContent = count;
      if (this.isFnWishlist) update_wishlist_page_meta_count_fn();
      if (this.closest('.hdt-site-nav_wishlist')) {
        this.toggleAttribute('hidden', count === 0);
      }
    };
    updateCount();
    document.addEventListener(`theme4:${this.prefix}:update`, (e) => {
      //console.log(this.prefix + ' count update.');
      updateCount();
    });
  }
  get isFnWishlist() {
    return true;
  }
  get array() {
    return this.isFnWishlist ? arr_wishlist_list : arr_compare_list;
  }
  get prefix() {
    return this.isFnWishlist ? 'wishlist' : 'compare';
  }
}
// Update link page
class WishlistLink extends HTMLElement {
  constructor() {
    super();
    $4('a', this).href = conver_to_link_fn(this.prefix, this.array);
    //console.log('WishlistLink', this.href)
    document.addEventListener(`theme4:${this.prefix}:update`, (e) => {
      $4('a', this).href = conver_to_link_fn(this.prefix, this.array);
    });
  }
  get isFnWishlist() {
    return true;
  }
  get array() {
    return this.isFnWishlist ? arr_wishlist_list : arr_compare_list;
  }
  get prefix() {
    return this.isFnWishlist ? 'wishlist' : 'compare';
  }
}
document.addEventListener('gh:wishlist:snapshot', (event) => {
  event.detail?.respond?.({
    href: conver_to_link_fn('wishlist', arr_wishlist_list),
    count: get_wishlist_entry_count_fn(),
    items: arr_wishlist_list.map((productId) => ({
      productId: String(productId),
      variantIds: get_wishlist_variant_entries_fn(productId).map((variant) => String(variant.id))
    }))
  });
});

customElements.define("hdt-wishlist", Wishlist);
customElements.define("hdt-wishlist-count", WishlistCount);
customElements.define("hdt-wishlist-a", WishlistLink);

['variant:update', 'variant:change', 'variant:changed', 'variant:selected', 'theme4:wishlist:update'].forEach((eventName) => {
  document.addEventListener(eventName, () => schedule_wishlist_variant_buttons_update_fn());
});

document.addEventListener('change', (event) => {
  if (!event.target.matches?.('.hdt-color-list input[data-vid], input[name="id"], select[name="id"]')) return;
  const root = event.target.closest('hdt-card-product, .shopify-section, hdt-product-info') || document;
  schedule_wishlist_variant_buttons_update_fn(root);
});

function add_product_to_wishlist_fn(productId, productHandle, variantData = null) {
  productId = `${productId}`;
  if (!productId) return Promise.resolve();
  if (has_wishlist_product_fn(productId)) {
    set_wishlist_variant_fn(productId, variantData);
    document.dispatchEvent(new CustomEvent('theme4:wishlist:update', {
      bubbles: true,
      detail: 'the4'
    }));
    return Promise.resolve();
  }

  if ($wishlist_list) {
    return fetch(linkWishlistApp, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        product_id: productId,
        product_handle: productHandle,
        action: 'add'
      })
    })
    .then((response) => response.json())
    .then((data) => {
      if (data.status != 'success') {
        throw new Error(data.message || 'Wishlist add failed');
      }
      arr_wishlist_list = JSON.parse(data.response.metafield.value).ecomrise_ids;
      if (!Array.isArray(arr_wishlist_list)) {
        arr_wishlist_list = arr_wishlist_list.split(',');
      }
      set_wishlist_variant_fn(productId, variantData);
      document.dispatchEvent(new CustomEvent('theme4:wishlist:update', {
        bubbles: true,
        detail: 'the4'
      }));
    });
  }

  arr_wishlist_list.unshift(productId);
  if (arr_wishlist_list.length > limitWishlist) {
    const arraySplice = arr_wishlist_list.splice(limitWishlist, 1);
    remove_wishlist_variant_fn(arraySplice[0]);
    $$4(`hdt-wishlist[data-id="${arraySplice[0]}"]`).forEach(btn => {
      btn.setAttribute('action', 'add');
    });
  }
  set_wishlist_variant_fn(productId, variantData);
  localStorage.setItem(nameCachedWishlist, arr_wishlist_list.toString());
  document.dispatchEvent(new CustomEvent('theme4:wishlist:update', {
    bubbles: true,
    detail: 'the4'
  }));
  return Promise.resolve();
}

let pendingSaveForLaterButton = null;

function save_line_for_later_fn(button) {
  if (!button || button.getAttribute('aria-busy') === 'true') return Promise.resolve();
  publish_custom_analytics_event_fn('cart_save_for_later', { save_scope: 'line', item_count: 1 }, true);
  button.setAttribute('aria-busy', 'true');
  return add_product_to_wishlist_fn(button.dataset.productId, button.dataset.productHandle, get_variant_data_from_wishlist_button_fn(button))
    .then(() => {
      show_wishlist_toast_fn(conver_to_link_fn('wishlist', arr_wishlist_list));
      const removeButton = button.closest('.hdt-line-item, .hdt-cart-item')?.querySelector('wrapp-remove-item-oncart');
      if (removeButton) {
        removeButton.click();
        return;
      }
      if (button.dataset.line) {
        return fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            line: Number(button.dataset.line),
            quantity: 0
          })
        }).then(() => window.location.reload());
      }
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      button.setAttribute('aria-busy', 'false');
    });
}

function open_save_line_later_modal_fn(button) {
  pendingSaveForLaterButton = button;
  const modal = $id4('CartSaveLineLaterModal');
  if (!modal) {
    save_line_for_later_fn(button);
    return;
  }
  open_cart_confirm_modal_fn('CartSaveLineLaterModal');
}

class SaveForLater extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.save.bind(this));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.save(event);
    });
  }
  save(event) {
    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true') return;
    open_save_line_later_modal_fn(this);
  }
}

function open_cart_confirm_modal_fn(id) {
  const modal = $id4(id);
  if (!modal) return;
  if (typeof modal.showModal === 'function') {
    modal.showModal();
  } else {
    modal.hidden = false;
    modal.setAttribute('open', '');
    modal.querySelector('[data-hdt-cart-modal-close]')?.focus?.({ preventScroll: true });
  }
}

function close_cart_confirm_modal_fn(modal) {
  if (!modal) return;
  if (typeof modal.close === 'function') {
    modal.close();
  } else {
    modal.removeAttribute('open');
    modal.hidden = true;
  }
}

function get_cart_url_fn() {
  const routesRoot = Shopify?.routes?.root || '/';
  return `${routesRoot.replace(/\/?$/, '/')}cart`;
}

function clear_cart_then_reload_fn() {
  document.dispatchEvent(new CustomEvent('theme:loading:doing', { bubbles: true }));
  return fetch(`${Shopify.routes.root}cart/clear.js`, {
    method: 'POST',
    headers: {
      Accept: 'application/json'
    }
  })
  .then(() => {
    window.location.href = get_cart_url_fn();
  })
  .finally(() => {
    document.dispatchEvent(new CustomEvent('theme:loading:done', { bubbles: true }));
  });
}

class CartClear extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.open.bind(this));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.open(event);
    });
  }
  open(event) {
    event.preventDefault();
    open_cart_confirm_modal_fn(this.getAttribute('aria-controls'));
  }
}

class CartSaveAll extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.open.bind(this));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.open(event);
    });
  }
  open(event) {
    event.preventDefault();
    open_cart_confirm_modal_fn(this.getAttribute('aria-controls'));
  }
}

class WishlistAddAllToCart extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.add.bind(this));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.add(event);
    });
  }
  get items() {
    const dataEl = $id4(this.dataset.itemsId);
    if (!dataEl) return [];
    try {
      const data = JSON.parse(dataEl.textContent || '[]');
      if (this.hasAttribute('data-exact-variants')) return data;
      return data.flatMap((item) => {
        const productId = item.product_id || item.productId || item.product;
        const savedVariants = get_wishlist_variant_entries_fn(productId);
        if (!savedVariants.length) {
          return [{
            id: Number(item.id),
            quantity: item.quantity || 1
          }];
        }
        return savedVariants.map((savedVariant) => ({
          id: Number(savedVariant.id),
          quantity: item.quantity || 1
        }));
      });
    } catch (error) {
      console.error(error);
      return [];
    }
  }
  add(event) {
    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true' || this.getAttribute('aria-disabled') === 'true') return;
    const items = this.items;
    if (!items.length) return;
    const cartDrawer = $4('hdt-cart-drawer');
    const cartDrawerSectionId = cartDrawer?.getAttribute('section-id');
    const cartSections = cartDrawerSectionId ? [cartDrawerSectionId] : [];
    let cartAdded = false;
    const shouldOpenDrawer = !!cartDrawer && themeHDN.settings.pageType !== 'cart';
    this.setAttribute('aria-busy', 'true');
    document.dispatchEvent(new CustomEvent('theme:loading:doing', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:push:section.id', { bubbles: true, detail: { sections: cartSections } }));
    const uniqueCartSections = [...new Set(cartSections.filter(Boolean))];
    fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items,
        sections_url: window.location.pathname,
        sections: uniqueCartSections.join(',')
      })
    })
    .then((response) => {
      if (!response.ok) throw new Error('Wishlist add to cart failed');
      return response.json();
    })
    .then(async (cartAddResponse) => {
      cartAdded = true;
      publish_custom_analytics_event_fn('wishlist_add_list_to_cart', { interaction_source: this.closest('gh-wishlist-drawer') ? 'drawer' : 'wishlist_page', item_count: items.length }, true);
      const wishlistDialog = this.closest('gh-wishlist-drawer')?.querySelector('dialog');
      if (wishlistDialog) {
        wishlistDialog.setAttribute('data-cart-handoff', '');
        wishlistDialog.close();
      }
      if (!shouldOpenDrawer) {
        window.location.href = this.dataset.redirectUrl || get_cart_url_fn();
        return;
      }

      if (!cartAddResponse.sections?.[cartDrawerSectionId]) {
        const cartRefreshResponse = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            sections_url: window.location.pathname,
            sections: uniqueCartSections.join(',')
          })
        });
        if (cartRefreshResponse.ok) {
          const cartRefresh = await cartRefreshResponse.json();
          cartAddResponse.sections = cartRefresh.sections || cartAddResponse.sections;
        }
      }

      if (!cartAddResponse.sections?.[cartDrawerSectionId]) {
        window.location.href = this.dataset.redirectUrl || get_cart_url_fn();
        return;
      }

      document.dispatchEvent(new CartUpdateEvent(
        {},
        this.id || cartDrawerSectionId,
        cartAddResponse,
        'open_cart_drawer',
        'product-form-component'
      ));
      this.setAttribute('aria-busy', 'false');
      document.dispatchEvent(new CustomEvent('theme:loading:done', { bubbles: true }));
    })
    .catch((error) => {
      if (!cartAdded) publish_custom_analytics_event_fn('wishlist_add_list_error', { interaction_source: this.closest('gh-wishlist-drawer') ? 'drawer' : 'wishlist_page', error_code: 'request_failed' });
      this.dispatchEvent(new CustomEvent('gh:wishlist:cart-error', { bubbles: true }));
      console.error(error);
      this.setAttribute('aria-busy', 'false');
      document.dispatchEvent(new CustomEvent('theme:loading:done', { bubbles: true }));
    });
  }
}

function get_cart_save_later_items_fn() {
  const dataEl = $id4('hdt-cart-save-later-items');
  if (!dataEl) return [];
  try {
    return JSON.parse(dataEl.textContent || '[]');
  } catch (error) {
    console.error(error);
    return [];
  }
}

function save_all_cart_items_fn() {
  const items = get_cart_save_later_items_fn();
  if (!items.length) return Promise.resolve();
  publish_custom_analytics_event_fn('cart_save_for_later', { save_scope: 'all', item_count: items.length }, true);
  document.dispatchEvent(new CustomEvent('theme:loading:doing', { bubbles: true }));
  return items.reduce((promise, item) => {
    return promise.then(() => add_product_to_wishlist_fn(item.id, item.handle, item));
  }, Promise.resolve())
  .then(() => {
    show_wishlist_toast_fn(conver_to_link_fn('wishlist', arr_wishlist_list));
    return clear_cart_then_reload_fn();
  })
  .catch((error) => {
    console.error(error);
  })
  .finally(() => {
    document.dispatchEvent(new CustomEvent('theme:loading:done', { bubbles: true }));
  });
}

let wishlistVariantPageData = null;
function get_wishlist_variant_page_data_fn() {
  if (wishlistVariantPageData) return wishlistVariantPageData;
  const dataEl = document.querySelector('[id^="WishlistVariantData-"]');
  if (!dataEl) {
    wishlistVariantPageData = {};
    return wishlistVariantPageData;
  }
  try {
    wishlistVariantPageData = JSON.parse(dataEl.textContent || '{}');
  } catch (error) {
    console.error(error);
    wishlistVariantPageData = {};
  }
  return wishlistVariantPageData;
}

function with_variant_url_fn(url, variantId) {
  if (!url || !variantId) return url;
  try {
    const nextUrl = new URL(url, window.location.origin);
    nextUrl.searchParams.set('variant', variantId);
    return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
  } catch (error) {
    const splitUrl = url.split('#');
    const hash = splitUrl[1] ? `#${splitUrl[1]}` : '';
    const base = splitUrl[0].split('?')[0];
    return `${base}?variant=${variantId}${hash}`;
  }
}

function update_wishlist_card_image_fn(card, variant) {
  const image = variant?.image;
  if (!image?.src) return;
  const media = card.querySelector('.hdt-card-product__media--main');
  if (!media || media.tagName !== 'IMG') return;
  media.src = image.src;
  if (image.srcset) media.srcset = image.srcset;
  if (image.width) media.width = image.width;
  if (image.height) media.height = image.height;
  if (image.alt) media.alt = image.alt;
}

const variantHoverRequests = new WeakMap();

function release_variant_image_hold_fn(card, request) {
  if (variantHoverRequests.get(card) !== request || !request.holdover) return;
  const holdover = request.holdover;
  holdover.classList.add('hdt-variant-image-hold--ready');
  request.releaseTimer = setTimeout(() => holdover.remove(), 180);
}

async function update_variant_hover_image_fn(card, input) {
  if (!card || !input?.hasAttribute('data-vhover')) return;
  const previousRequest = variantHoverRequests.get(card);
  if (previousRequest?.image && !previousRequest.image.complete) previousRequest.image.removeAttribute('src');
  if (previousRequest?.releaseTimer) clearTimeout(previousRequest.releaseTimer);
  let holdover = previousRequest?.holdover?.isConnected ? previousRequest.holdover : null;
  if (holdover) holdover.classList.remove('hdt-variant-image-hold--ready');
  const mainBeforeChange = card.querySelector('.hdt-card-product__media--main');
  if (!holdover && mainBeforeChange?.complete && mainBeforeChange.naturalWidth) {
    holdover = mainBeforeChange.cloneNode(false);
    holdover.className = 'hdt-variant-image-hold';
    holdover.removeAttribute('srcset');
    holdover.removeAttribute('sizes');
    holdover.src = mainBeforeChange.currentSrc || mainBeforeChange.src;
    holdover.alt = '';
    holdover.setAttribute('aria-hidden', 'true');
    holdover.loading = 'eager';
    mainBeforeChange.parentElement.append(holdover);
  }
  const request = { image: null, holdover, releaseTimer: null };
  variantHoverRequests.set(card, request);
  const hover = card.querySelector('.hdt-card-product__media--hover');
  hover?.style.setProperty('visibility', 'hidden', 'important');
  if (holdover) {
    queueMicrotask(async () => {
      const nextMain = card.querySelector('.hdt-card-product__media--main');
      if (nextMain?.tagName === 'IMG') {
        if (nextMain.complete && nextMain.naturalWidth) {
          if (variantHoverRequests.get(card) === request) holdover.remove();
          return;
        }
        try { await nextMain.decode(); } catch { /* Keep the card usable if the image fails. */ }
      }
      release_variant_image_hold_fn(card, request);
    });
  }
  const url = input.dataset.vhover;
  if (!url || !matchMedia('(pointer: fine)').matches) return;

  const nextHover = new Image();
  request.image = nextHover;
  nextHover.src = url;
  try {
    await nextHover.decode();
  } catch {
    return;
  }
  if (variantHoverRequests.get(card) !== request || !card.isConnected) return;

  const main = card.querySelector('.hdt-card-product__media--main');
  if (!main || main.tagName !== 'IMG') return;
  const currentHover = card.querySelector('.hdt-card-product__media--hover');
  nextHover.className = currentHover?.className || main.className.replace('hdt-card-product__media--main', 'hdt-card-product__media--hover');
  nextHover.alt = input.dataset.vhoverAlt || '';
  nextHover.width = nextHover.naturalWidth;
  nextHover.height = nextHover.naturalHeight;
  nextHover.sizes = currentHover?.sizes || main.sizes;
  nextHover.loading = 'lazy';
  nextHover.decoding = 'async';
  if (card.matches(':hover')) nextHover.style.opacity = '0';
  if (currentHover) currentHover.replaceWith(nextHover);
  else main.parentElement.append(nextHover);
  if (nextHover.style.opacity) {
    // Start from the current image, then use the theme's existing hover transition.
    getComputedStyle(nextHover).opacity;
    requestAnimationFrame(() => {
      if (variantHoverRequests.get(card) === request && nextHover.isConnected) {
        nextHover.style.removeProperty('opacity');
      }
    });
  }
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!input.matches?.('.hdt-color-list input[data-vhover]')) return;
  update_variant_hover_image_fn(input.closest('hdt-card-product'), input);
}, true);

function select_wishlist_card_swatch_fn(card, variant) {
  if (!variant?.id) return;
  const optionValues = [variant.option1, variant.option2, variant.option3, ...(variant.options || [])]
    .filter(Boolean)
    .map((value) => `${value}`.toLowerCase());
  let input = card.querySelector(`.hdt-color-list input[data-vid="${variant.id}"]`);
  if (!input && optionValues.length) {
    input = Array.from(card.querySelectorAll('.hdt-color-list input[type="radio"]'))
      .find((radio) => optionValues.includes(`${radio.value}`.toLowerCase()));
  }
  if (!input) return;
  card.querySelectorAll(`input[name="${input.name}"]`).forEach((radio) => {
    radio.checked = false;
    radio.removeAttribute('checked');
  });
  input.checked = true;
  input.setAttribute('checked', 'checked');
  update_variant_hover_image_fn(card, input);
}

function get_hydratable_wishlist_variant_fn(productData, savedVariant) {
  if (!savedVariant?.id) return null;
  return {
    ...savedVariant,
    ...(productData?.variants?.[savedVariant.id] || {})
  };
}

function hydrate_wishlist_card_variant_fn(card, productId, variant, productData = null) {
  if (!card || !productId || !variant?.id) return;
  const titleLink = card.querySelector('.hdt-card-product__title');
  const productTitle = productData?.title || titleLink?.textContent?.trim() || '';
  const productUrl = variant.url || with_variant_url_fn(productData?.url || titleLink?.getAttribute('href') || '', variant.id);
  card.setAttribute('data-wishlist-variant-id', variant.id);
  card.setAttribute('data-wishlist-product-id', productId);
  card.setAttribute('data-wishlist-product-title', productTitle);
  card.setAttribute('data-wishlist-product-url', productUrl);
  card.setAttribute('data-wishlist-variant-title', variant.title || '');
  card.setAttribute('data-wishlist-variant-sku', variant.sku || '');
  card.querySelectorAll('[data-pr-url]').forEach((link) => {
    link.setAttribute('href', with_variant_url_fn(link.getAttribute('href'), variant.id));
  });
  card.querySelectorAll('hdt-wishlist[data-id]').forEach((button) => {
    button.dataset.variantId = variant.id;
    button.dataset.variantTitle = variant.title || '';
    button.dataset.variantUrl = variant.url || '';
    button.dataset.variantSku = variant.sku || '';
    button.dataset.variantOption1 = variant.option1 || '';
    button.dataset.variantOption2 = variant.option2 || '';
    button.dataset.variantOption3 = variant.option3 || '';
    button.setAttribute('action', 'remove');
  });
  update_wishlist_card_image_fn(card, variant);
  select_wishlist_card_swatch_fn(card, variant);
}

function escape_wishlist_selector_value_fn(value) {
  if (window.CSS?.escape) return CSS.escape(value);
  return `${value}`.replace(/["\\]/g, '\\$&');
}

function uniquify_wishlist_card_controls_fn(card, productId, variantId) {
  if (!card || !productId || !variantId) return;
  const suffix = `wishlist-${productId}-${variantId}`;
  card.querySelectorAll('[id]').forEach((element) => {
    const oldId = element.id;
    if (!oldId || oldId.endsWith(`-${suffix}`)) return;
    const newId = `${oldId}-${suffix}`;
    card.querySelectorAll(`label[for="${escape_wishlist_selector_value_fn(oldId)}"]`).forEach((label) => {
      label.setAttribute('for', newId);
    });
    element.id = newId;
  });
  card.querySelectorAll('input[type="radio"][name], input[type="checkbox"][name]').forEach((input) => {
    if (input.name.endsWith(`-${suffix}`)) return;
    input.name = `${input.name}-${suffix}`;
  });
}

function prepare_wishlist_variant_clone_fn(card, productId, variantId) {
  if (!card) return;
  card.removeAttribute('data-wishlist-variant-hydrated');
  card.removeAttribute('data-wishlist-variant-id');
  [card, ...card.querySelectorAll('[observed]')].forEach((element) => {
    element.removeAttribute('observed');
  });
  [card, ...card.querySelectorAll('.hdt-reveal--offscreen, .hdt-reveal--cancel, .hdt-reveal--design-mode')].forEach((element) => {
    element.classList.remove('hdt-reveal--offscreen', 'hdt-reveal--cancel', 'hdt-reveal--design-mode');
  });
  card.setAttribute('animationend', '');
  card.style.opacity = '1';
  card.querySelectorAll('[hdt-reveal]').forEach((element) => {
    element.setAttribute('animationend', '');
  });
  uniquify_wishlist_card_controls_fn(card, productId, variantId);
}

function hydrate_wishlist_variant_cards_fn(root = document) {
  if (!window.isPageWishlist) return;
  const pageData = get_wishlist_variant_page_data_fn();
  root.querySelectorAll('.hdt-main-wishlist hdt-card-product').forEach((card) => {
    if (card.hasAttribute('data-wishlist-variant-hydrated')) return;
    const productId = card.getAttribute('p-id');
    const productData = pageData[productId];
    const savedVariants = get_wishlist_variant_entries_fn(productId);
    if (!productId || !savedVariants.length) return;
    let insertAfter = card;
    const baseCard = card.cloneNode(true);
    savedVariants.forEach((savedVariant, index) => {
      const variant = get_hydratable_wishlist_variant_fn(productData, savedVariant);
      if (!variant) return;
      const targetCard = index === 0 ? card : baseCard.cloneNode(true);
      if (index === 0) {
        uniquify_wishlist_card_controls_fn(targetCard, productId, variant.id);
      } else {
        prepare_wishlist_variant_clone_fn(targetCard, productId, variant.id);
      }
      if (index > 0) {
        insertAfter.after(targetCard);
        insertAfter = targetCard;
      }
      targetCard?.setAttribute('data-wishlist-variant-hydrated', '');
      hydrate_wishlist_card_variant_fn(targetCard, productId, variant, productData);
    });
  });
  update_wishlist_page_meta_count_fn();
  document.dispatchEvent(new CustomEvent('theme4:wishlist:hydrated'));
}

let wishlistVariantHydrateRaf = 0;
function schedule_wishlist_variant_cards_hydration_fn(root = document) {
  if (!window.isPageWishlist) return;
  if (wishlistVariantHydrateRaf) cancelAnimationFrame(wishlistVariantHydrateRaf);
  wishlistVariantHydrateRaf = requestAnimationFrame(() => {
    wishlistVariantHydrateRaf = 0;
    hydrate_wishlist_variant_cards_fn(root);
  });
}

customElements.define('hdt-save-for-later', SaveForLater);
customElements.define('hdt-cart-clear', CartClear);
customElements.define('hdt-cart-save-all', CartSaveAll);
customElements.define('hdt-wishlist-add-all', WishlistAddAllToCart);

schedule_wishlist_variant_cards_hydration_fn();
document.addEventListener('theme4:wishlist:update', () => schedule_wishlist_variant_cards_hydration_fn());
document.addEventListener('products:update', (event) => schedule_wishlist_variant_cards_hydration_fn(event.target || document));

document.addEventListener('click', (event) => {
  if (event.target.matches?.('.hdt-cart-confirm-modal')) {
    const modal = event.target;
    if (modal.id === 'CartSaveLineLaterModal') pendingSaveForLaterButton = null;
    close_cart_confirm_modal_fn(modal);
    return;
  }

  const closeButton = event.target.closest('[data-hdt-cart-modal-close]');
  if (closeButton) {
    const modal = closeButton.closest('.hdt-cart-confirm-modal');
    if (modal?.id === 'CartSaveLineLaterModal') pendingSaveForLaterButton = null;
    close_cart_confirm_modal_fn(modal);
  }

  if (event.target.closest('[data-hdt-cart-clear-confirm]')) {
    close_cart_confirm_modal_fn(event.target.closest('.hdt-cart-confirm-modal'));
    publish_custom_analytics_event_fn('cart_clear', { clear_scope: 'all' }, true);
    clear_cart_then_reload_fn();
  }

  if (event.target.closest('[data-hdt-cart-save-confirm]')) {
    const modal = event.target.closest('.hdt-cart-confirm-modal');
    close_cart_confirm_modal_fn(modal);
    save_all_cart_items_fn();
  }

  if (event.target.closest('[data-hdt-cart-save-line-confirm]')) {
    const modal = event.target.closest('.hdt-cart-confirm-modal');
    const button = pendingSaveForLaterButton;
    pendingSaveForLaterButton = null;
    close_cart_confirm_modal_fn(modal);
    save_line_for_later_fn(button);
  }
});

document.addEventListener('cancel', (event) => {
  if (event.target.matches?.('dialog.hdt-cart-confirm-modal') && event.target.id === 'CartSaveLineLaterModal') {
    pendingSaveForLaterButton = null;
  }
}, true);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const modal = $id4('CartSaveLineLaterModal');
  if (!modal || modal.hidden) return;
  pendingSaveForLaterButton = null;
  close_cart_confirm_modal_fn(modal);
});

// COMPARE
class Compare extends Wishlist {
  get isFnWishlist() {
    return false;
  }
  get textFn() {
    return 'compare';
  }
}
// Update count
class CompareCount extends WishlistCount {
  get isFnWishlist() {
    return false;
  }
}
// Update link page
class CompareLink extends WishlistLink {
  get isFnWishlist() {
    return false;
  }
}
customElements.define("hdt-compare", Compare);
customElements.define("hdt-compare-count", CompareCount);
customElements.define("hdt-compare-a", CompareLink);

// --------------------------
// Product Recommendations
// --------------------------
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }
          document.dispatchEvent(new CustomEvent("currency:update"));
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('product-recommendations', ProductRecommendations);


// --------------------------
// Product Recently
// --------------------------
class ProductRecently extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      let handleProducts = localStorage.getItem("theme4:recently:id");
      let prdId = this.dataset.id;

      if (handleProducts !== null) {
        let products = handleProducts.split(',');
        let url = this.dataset.url.replace('q=', 'q=id:' + products.join("%20OR%20id:"));
        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const recently = html.querySelector('product-recently');

            if (recently && recently.innerHTML.trim().length) {
              this.innerHTML = recently.innerHTML;
            }
            document.dispatchEvent(new CustomEvent("currency:update"));
          })
          .catch((e) => {
            console.error(e);
          });

      }
      let arrayProducts = handleProducts !== null ? handleProducts.split(',') : new Array;
      if (!arrayProducts.includes(prdId + '')) {
        if (arrayProducts.length >= 10) {
          arrayProducts.pop();
        }
        function prepend(value, array) {
          var newArray = array.slice();
          newArray.unshift(value);
          return newArray;
        }
        arrayProducts = prepend(prdId, arrayProducts);
        arrayProducts = arrayProducts.toString();
        localStorage.setItem("theme4:recently:id", arrayProducts);
      }
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('product-recently', ProductRecently);


// --------------------------
// Predictive Search
// --------------------------
class PredictiveSearch extends DrawerComponent {
  constructor() {
    super();
    // console.log(!this.hasAttribute('enabled'))
    if (!this.hasAttribute('enabled')) return;
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-results-search]');
    this.input = this.querySelector('input[type="search"]');
    this.sectionIdResults = this.getAttribute('data-section-id-results') || 'hdt_predictive-search';
    this.allPredictiveSearchInstances = $$4('hdt-predictive-search');
    this.skeleton = this.querySelector('[data-skeleton-search]');
    this.noResults = this.querySelector('.hdt-cart-no-results');
    this.hideWrapper = this.querySelector('.hdt-cart-hide-has-results');
    this.showWrapper = this.querySelector('.hdt-cart-show-has-results');
    this.btn_viewall = this.querySelector('.hdt-view_all');
    this.href = this.btn_viewall?.getAttribute("href");
    this.isOpen = false;
    this.abortController2 = new AbortController();
    this.searchTerm = '';
    if (this.input) {
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    const formData = new FormData(this.input.form);
    this.params = "";
    for (const [key, value] of formData) {
      if (key === 'q') break;
      this.params += `${key}=${value}&`;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    //this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      // this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    this.btn_viewall?.setAttribute("href", this.href.replace(/\/search\?q=.*/g, "/search?q=" + this.searchTerm));

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.openSearch();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    // switch (event.code) {
    //   case 'ArrowUp':
    //     this.switchOption('up');
    //     break;
    //   case 'ArrowDown':
    //     this.switchOption('down');
    //     break;
    //   case 'Enter':
    //     this.selectOption();
    //     break;
    // }
  }

  // onKeydown(event) {
  //   // Prevent the cursor from moving in the input when using the up and down arrow keys
  //   if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
  //     event.preventDefault();
  //   }
  // }
  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (!previousTerm || currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.trim().toLowerCase();
    this.abortController2.abort();
    this.abortController2 = new AbortController();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${themeHDN.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${this.params}section_id=${this.sectionIdResults}`, {
      signal: this.abortController2.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.getQuery() !== searchTerm) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-' + this.sectionIdResults).innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.hideWrapper.style.cssText = "display: none;";
    this.showWrapper.style.cssText = "display: block;";
    this.skeleton.classList.remove("hdt-hidden");

    this.setAttribute('loading', true);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.openSearch();
    document.dispatchEvent(new CustomEvent("currency:update"));
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }

  openSearch() {
    //this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.skeleton.classList.add("hdt-hidden");
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.hideWrapper.style.cssText = "display: block;";
      this.showWrapper.style.cssText = "display: none;";
      this.predictiveSearchResults.innerHTML = "";
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    //this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('hdt-predictive-search', PredictiveSearch);
// bundle product

const bundleProduct = () => {
  if (window.innerWidth < 768) {
    return;
  }
  let arrBundle = $$4("[data-bundle-pin]");
  if (!arrBundle) {
    return;
  }
  arrBundle.forEach((el) => {
    if (!el.dataset.trigger) {
      return;
    } else {
      el.onmouseover = () => {
        $4(el.dataset.trigger).classList.add("is--hover");
        $4(el.dataset.trigger)
          .closest("[data-bundle-parent]")
          .classList.add("has--hover");
      };
      el.onmouseleave = () => {
        $4(el.dataset.trigger).classList.remove("is--hover");
        $4(el.dataset.trigger)
          .closest("[data-bundle-parent]")
          .classList.remove("has--hover");
      };
    }
  });
};
bundleProduct();

// --------------------------
// Back to top
// --------------------------
class BackToTop extends HTMLElement {
  constructor() {
    super();
    this.config = JSON.parse(this.getAttribute('config'));
    if (window.innerWidth < 768 && this.config.hiddenMobile) return;
    this.debounce_timer = 0;
    this.debounce_timer2 = 0;
    this.circlechart = this.querySelector('.hdt-circle-css');
    this.addEventListener("click", this.goTop.bind(this));
    window.addEventListener("scroll", this.backToTop.bind(this));
  }
  goTop() {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }

  backToTop() {
    let self = this;
    if (this.debounce_timer) { clearTimeout(this.debounce_timer); }
    this.debounce_timer = setTimeout(function () {
      if (window.scrollY > self.config.scrollTop) {
        self.classList.add('is--show');
      } else {
        self.classList.remove('is--show');
      }
    }, 40);

    if (!this.circlechart) return;

    if (this.debounce_timer2) { clearTimeout(this.debounce_timer2); }

    this.debounce_timer2 = setTimeout(function () {
      let scrollTop2 = window.scrollY,
        docHeight = document.body.offsetHeight,
        winHeight = window.innerHeight,
        scrollPercent = scrollTop2 / (docHeight - winHeight),
        degrees = scrollPercent * 360;
      self.circlechart.style.setProperty("--cricle-degrees", degrees + 'deg');
    }, 6);
  }
}

customElements.define('back-to-top', BackToTop);





class cookiesBar extends HTMLElement {
  constructor() {
    super();
    this.cookies = JSON.parse(this.getAttribute("configs"));
    this.cookies.cookiesName = "theme4:cookies";
    this.cookies.drawer = this.querySelector("hdt-drawer");
    this.isShowCookiesAll = (this.cookies.show == '1');
    this.acceptBtn = this.cookies.drawer.querySelector(".hdt-pp_cookies__accept-btn");
    this.declineBtn = this.cookies.drawer.querySelector(".hdt-pp_cookies__decline-btn");
    this.loadCookieBanner(this);
    let self = this;
    this.acceptBtn?.addEventListener("click", this.handleAccept.bind(this));
    this.declineBtn?.addEventListener("click", this.handleDecline.bind(this));
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        const cookiesSelect = event.target.classList.contains('sys-cookies');
        if (!cookiesSelect) return;
        self.showCookiesBanner();
      })
      document.addEventListener('shopify:section:deselect', (event) => {
        const cookiesSelect = event.target.classList.contains('sys-cookies');
        if (!cookiesSelect) return;
        self.hideCookiesBanner();
      })
    }
  }
  handleAccept() {
    if (this.isShowCookiesAll) {
      setCookie(this.cookies.cookiesName, 'accepted', parseInt(this.cookies.day_next));
    }
    window.Shopify.customerPrivacy.setTrackingConsent(true, this.hideCookiesBanner.bind(this));
  }

  handleDecline() {
    if (this.isShowCookiesAll) {
      setCookie(this.cookies.cookiesName, 'accepted', parseInt(this.cookies.day_next));
    }
    window.Shopify.customerPrivacy.setTrackingConsent(false, this.hideCookiesBanner.bind(this));
  }

  showCookiesBanner() {
    this.cookies.drawer.open();
    // $4("html").classList.remove("no-scroll");
  }

  hideCookiesBanner() {
    this.cookies.drawer.close();
  }

  initCookieBanner() {
    const userCanBeTracked = window.Shopify.customerPrivacy.userCanBeTracked();
    const userTrackingConsent = window.Shopify.customerPrivacy.getTrackingConsent();

    if ((!userCanBeTracked && userTrackingConsent === 'no_interaction') || this.isShowCookiesAll) {
      if (!Shopify.designMode) {
        this.showCookiesBanner();
      }
    }
  }

  loadCookieBanner() {

    let self = this;
    if (this.cookies.drawer.length == 0) return;
    this.cookies.day_next = this.cookies.day_next || 60;
    this.isShowCookiesAll = (this.cookies.show == '1');
    if (getCookie(this.cookies.cookiesName) == 'accepted' || this.cookies.drawer.querySelector("dialog").hasAttribute("open")) return;
    window.Shopify.loadFeatures([
      {
        name: 'consent-tracking-api',
        version: '0.1',
      }
    ],
      function (error) {
        if (error) {
          throw error;
        }
        self.initCookieBanner();
      });
  }
}

customElements.define('sys-cookies', cookiesBar);

// --------------------------
// Newsletter
// --------------------------

class newsletterModal extends HTMLElement {
  constructor() {
    super();
    this.configs = JSON.parse(this.getAttribute('configs'));
    this.modal = this.querySelector("hdt-modal");
    this.dialog = this.querySelector("dialog");
    this.action_close = this.querySelectorAll('[action-close]');
    this.campaign = this.configs.campaign || 'welcome10-v2';
    this.stateKey = 'hdt_newsletter_welcome10_v2';
    this.sessionKey = this.stateKey + ':session';
    this.promptCoordinator = getPromptCoordinator();
    this.pendingOutcome = 'interrupted';
    this.outcomeFinalized = false;
    this.promptSuspended = false;
    this.emittingOutcome = false;
    this.onNewsletterOutcome = (event) => {
      if (this.emittingOutcome || !event.detail || !event.detail.outcome) return;
      this.finalizeOutcome(event.detail.outcome, false);
    };

    this.addEventListener('hdt:newsletter:outcome', this.onNewsletterOutcome);
    this.setupCloseEvents();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        const newsletterSelect = event.target.classList.contains('sys-newsletter');
        if (!newsletterSelect) return;
        this.modal.open();
      })
      document.addEventListener('shopify:section:deselect', (event) => {
        const newsletterSelect = event.target.classList.contains('sys-newsletter');
        if (!newsletterSelect) return;
        this.modal.close();
      })
    }
    else {
      this.schedulePrompt();
    }
  }

  getStoredState() {
    let state = readJsonState(window.localStorage, this.stateKey);
    if (!state) {
      try {
        const fallback = getCookie(this.stateKey);
        state = fallback ? JSON.parse(fallback) : null;
      } catch (error) {
        state = null;
      }
    }
    if (state) return state;

    const legacyCookie = getCookie('theme4:newsletter:' + this.configs.id);
    if (legacyCookie === 'shown') {
      const now = Date.now();
      state = {
        version: 2,
        campaign: this.campaign,
        outcome: 'soft_dismiss',
        createdAt: now,
        expiresAt: now + getNewsletterSuppressionMs('soft_dismiss')
      };
      this.persistState(state, getNewsletterSuppressionMs('soft_dismiss'));
    }
    return state;
  }

  isIneligible() {
    if (this.configs.subscriber === true || this.configs.professional === true) return true;
    const sessionState = readJsonState(window.sessionStorage, this.sessionKey);
    if (sessionState && sessionState.shown === true) return true;
    const state = this.getStoredState();
    return Boolean(state && state.campaign === this.campaign && isFreshState(state));
  }

  schedulePrompt() {
    if (this.isIneligible() || !this.promptCoordinator) {
      this.modal.setAttribute('closed', '');
      return;
    }

    this.promptCoordinator.request({
      id: 'newsletter',
      priority: 10,
      source: 'automatic',
      minVisibleMs: 20000,
      after: { id: 'localization', delayMs: 12000 },
      pause: () => this.pauseNewsletter(),
      open: () => this.openNewsletter()
    });
  }

  persistState(state, suppressionMs) {
    if (writeJsonState(window.localStorage, this.stateKey, state)) return;
    const hours = Math.max(1, Math.ceil(suppressionMs / (60 * 60 * 1000)));
    setCookie(this.stateKey, JSON.stringify(state), null, hours);
  }

  writeOutcomeState(outcome) {
    const now = Date.now();
    const previous = this.getStoredState();
    const suppressionMs = getNewsletterSuppressionMs(outcome) || getNewsletterSuppressionMs('impression');
    this.persistState({
      version: 2,
      campaign: this.campaign,
      outcome,
      lastAction: outcome,
      lastImpressionAt: outcome === 'impression' ? now : (previous && previous.lastImpressionAt) || now,
      createdAt: now,
      expiresAt: now + suppressionMs
    }, suppressionMs);
  }

  openNewsletter() {
    if (!this.isConnected || (this.dialog && this.dialog.open)) return;
    this.modal.removeAttribute('closed');
    this.promptSuspended = false;
    this.pendingOutcome = 'impression';
    writeJsonState(window.sessionStorage, this.sessionKey, { shown: true, at: Date.now() });
    this.writeOutcomeState('impression');
    this.modal.open();
  }

  finalizeOutcome(outcome, emit = true) {
    if (this.outcomeFinalized) return;
    const allowed = ['soft_dismiss', 'declined', 'subscribed', 'interrupted'];
    const finalOutcome = allowed.includes(outcome) ? outcome : 'interrupted';
    this.outcomeFinalized = true;
    this.writeOutcomeState(finalOutcome);
    if (this.promptCoordinator) {
      this.promptCoordinator.complete('newsletter', {
        source: 'automatic',
        outcome: finalOutcome
      });
    }
    if (emit) {
      this.emittingOutcome = true;
      this.dispatchEvent(new CustomEvent('hdt:newsletter:outcome', {
        bubbles: true,
        detail: { outcome: finalOutcome }
      }));
      this.emittingOutcome = false;
    }
  }

  pauseNewsletter() {
    if (!this.dialog || !this.dialog.open) return;
    this.promptSuspended = true;
    this.pendingOutcome = 'interrupted';
    this.modal.close();
  }

  setupCloseEvents() {
    this.action_close.forEach((button) => {
      button.addEventListener('click', () => {
        this.pendingOutcome = button.dataset.newsletterOutcome === 'declined' ? 'declined' : 'soft_dismiss';
        this.modal.close();
      });
    });
    this.dialog.addEventListener('cancel', () => {
      this.pendingOutcome = 'soft_dismiss';
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.pendingOutcome = 'soft_dismiss';
    });
    this.dialog.addEventListener(`${dialogClose}`, () => {
      if (this.promptSuspended) {
        this.promptSuspended = false;
        this.modal.setAttribute('closed', '');
        return;
      }
      const outcome = this.pendingOutcome === 'impression' ? 'interrupted' : this.pendingOutcome;
      this.finalizeOutcome(outcome);
      this.modal.setAttribute('closed', '');
    });
  }

  disconnectedCallback() {
    this.removeEventListener('hdt:newsletter:outcome', this.onNewsletterOutcome);
    if (this.promptCoordinator && !this.outcomeFinalized) {
      this.promptCoordinator.cancel('newsletter');
    }
  }

}
customElements.define('sys-newsletter', newsletterModal);


// --------------------------
// Countdown Cart
// --------------------------

class cartCountdown extends HTMLElement {
  constructor() {
    super();
    let settMinutes = 60 * this.getAttribute("time"), time = parseInt(sessionStorage.getItem('cartTime')), display = this.querySelector('#timer_count');
    this.config = JSON.parse(this.getAttribute("config"));
    var self = this;
    function startTimer(duration, display) {
      let timer = duration, minutes, seconds;
      let countdown = setInterval(function () {
          minutes = parseInt(timer / 60, 10)
          seconds = parseInt(timer % 60, 10);
          minutes = minutes < 10 ? minutes : minutes;
          seconds = seconds < 10 ? "0" + seconds : seconds;
          display.textContent = minutes + self.config.labelm + " : " + seconds + self.config.labels;
          let calc = 60 * parseInt(timer / 60, 10) + parseInt(timer % 60, 10);
          if (!Shopify.designMode) {
            sessionStorage.setItem('cartTime', calc);
          }
          if (--timer < 0) {
            window.clearInterval(countdown);
            display.textContent = 0 + self.config.labelm + " : " + 0 + self.config.labels;
            fetch(Shopify.routes.root+"cart/clear.js")
            .then((response) => response.text())
            .then((text) => {
              console.log("Clear cart")
              window.location.href = get_cart_url_fn();
            })
            .catch((e) => {
              console.error(e);
            });
          }
      }, 1000);
    };
    if (time > 0) {startTimer(time, display);}
    else {startTimer(settMinutes, display)}
  }
}
customElements.define('cart-countdown', cartCountdown);

// --------------------------
// Form status
// --------------------------


const contactForm = $$4('form[action^="/contact"]');
contactForm.forEach(function(form) {
  form.addEventListener("submit", function(event){
    sessionStorage.setItem("the4:recentform", this.getAttribute("id"));
  })
});
const goToForm = (id) => {
  const form = $4(`#${id}`);
  const formParent = form.closest("hdt-modal");
  const buttonForm = $4(`[aria-controls="${formParent.id}"]`);
  // console.log(buttonForm)
  if (buttonForm) {
    buttonForm.scrollIntoView({ behavior: 'smooth' });
  }
}
var recentform = sessionStorage.getItem("the4:recentform") || "";

if (location.href.indexOf('contact_posted=true') > 0 && recentform !== "" ) {
  document.dispatchEvent( new CustomEvent('the4:recentform', { detail: { recentform: recentform }, bubbles: true, cancelable: true }) );
  $id4(recentform)?.classList.add("on-live");
  if ($4('.form-status-mirror-'+ recentform)) {
    $4('.form-status-mirror-'+ recentform).style.cssText = "display: block;";
  }
  goToForm(recentform);
}

else if (location.href.indexOf('customer_posted=true') > 0 && recentform !== "" ) {
  document.addEventListener('the4:recentform', (event)=>{
    let modal_id = document.getElementById(event.detail.recentform).getAttribute('data-id') || document.getElementById(event.detail.recentform).getAttribute('id');
    document.getElementById(modal_id)?.closest("hdt-modal:not([closed])")?.open();
  })
  document.dispatchEvent( new CustomEvent('the4:recentform', { detail: { recentform: recentform }, bubbles: true, cancelable: true }) );
  $id4(recentform)?.classList.add("on-live");
  goToForm(recentform);
}
else if (location.href.indexOf('contact_posted=true') > 0 && location.href.indexOf('#') > 0 ) {
  var recentform = location.href.split("#")[1];
  if ($id4(recentform).length > 0) {
    sessionStorage.setItem("the4:recentform", recentform);
    $id4(recentform).classList.add("on-live");
    if ($4('.form-status-mirror-'+ recentform)) {
      $4('.form-status-mirror-'+ recentform).style.cssText = "display: block;";
    }
    goToForm(recentform);
  }
}




// --------------------------
// Multi brands
// --------------------------
var hdtNavLoading = true;
class multiBrands extends HTMLElement {
  constructor() {
    super();
    if($id4("hdt-nav-ul").classList.value.includes("hdt-nav-loading") < 0){
      return false;
    }
    this.item = $$4("[item-brand]", this);
    // this.section_id = this.getAttribute("s_id");
    // this.url = "";
    if(this.item.length) {
      for(var i=0; i < this.item.length; i++) {
        if (this.item[i].parentElement.classList.value.includes("is--active")) {
          this.url = this.item[i].getAttribute("href");
          const lazyMb = $4('hdt-lazy-submenu#sub-hdt-nav-mb');
          if (lazyMb) {
            lazyMb.addEventListener('lazyhtml:added', () => $4(`#hdt-nav-ul-mb summary[data-url="${this.url}"]`)?.parentElement.setAttribute("open",""));
          } else {
            $4(`#hdt-nav-ul-mb summary[data-url="${this.url}"]`)?.parentElement.setAttribute("open","");
          }
          // localStorage.setItem("the4:brand:url", this.url);
          break;
        }
      }
      // if(this.url === "") {
      //   this.url = localStorage.getItem("the4:brand:url") || Shopify.routes.root;
      //   for(var i=0; i < this.item.length; i++) {
      //     if (this.item[i].getAttribute("href") === this.url) {
      //       this.item[i].parentElement.classList.add("is--active");
      //       $4("#hdt-nav-ul-mb summary[data-url=\""+ this.url +"\"]").parentElement.setAttribute("open","");
      //       if (i > 0) {
      //         this._init();
      //       }
      //       break;
      //     }
      //   }
      // }
    }
  }
  // _init(){
  //   var nameStorage = "the4:brand:nav" + this.url.replace(/\//g,'-');
  //   if(!Shopify.designMode && sessionStorage.getItem(nameStorage) !== null){
  //     $id4("hdt-nav-ul").innerHTML = sessionStorage.getItem(nameStorage);
  //     $id4("hdt-nav-ul").classList.remove("hdt-nav-loading");
  //     return;
  //   }
  //   if (!hdtNavLoading) {
  //     return;
  //   }
  //   fetch(this.url + "?section_id=" + this.section_id)
  //     .then((response) => response.text())
  //     .then((text) => {
  //       const html = document.createElement('div');
  //       html.innerHTML = text;
  //       const nav = html.querySelector('#hdt-nav-ul');
  //       if (nav && nav.innerHTML.trim().length) {
  //         $id4("hdt-nav-ul").innerHTML = nav.innerHTML;
  //         $id4("hdt-nav-ul").classList.remove("hdt-nav-loading");
  //         sessionStorage.setItem(nameStorage, nav.innerHTML.trim().replace(/^\s+|\s+$|\n/gm,''));
  //       }
  //     })
  //     .catch((e) => {
  //       console.error(e);
  //     });
  //   hdtNavLoading = false;
  // }
}

customElements.define('multi-brands', multiBrands);

// --------------------------
// Footer Accordion
// --------------------------

class FooterAccordion extends HTMLElement{
  constructor(){
    super();
    this.id = this.getAttribute('id');
    this.heading = $4(`#Heading-${this.id}`,this);
    this.content = $4(`#Content-${this.id}`,this);
    this.config = JSON.parse(this.getAttribute('config'));
    this.flag = false;
    if(!this.config){
      this.config = {
        accordion: true
      }
    }
    // if(window.innerWidth > 767){
    //   return;
    // }
    if(this.config.accordionMobile){
      this.init();
      window.addEventListener('resize', () => {
        window.innerWidth > 767 ? this.removeAttribute('open') : this.flag ? " " : this.init();
      })
    }
  }
  static observedAttributes = ['open'];
  get open(){
    return this.hasAttribute('open');
  }
  attributeChangedCallback(name, oldValue, newValue) {
    this.open ? this.content.style.setProperty('height', `${this.content.scrollHeight}px`) : this.content.style.removeProperty('height');
  }
  init(){
    window.innerWidth < 768 ? this.handleHeadingClick() : this.removeAttribute('open');
  }
  handleHeadingClick(){
    this.flag = true;
    this.heading.addEventListener('click', () =>{
      if(window.innerWidth > 767) return;
      this.open ? this.removeAttribute('open') : this.setAttribute('open', '');
    })
  }
}
customElements.define('hdt-faccordion',FooterAccordion);





//  //


class countryFilter extends HTMLElement {
  constructor() {
    super();
    this.search = $4('[name=country_filter]', this);
    this.reset = $4(".hdt-country_filter__reset", this);
    if (this.search) {
      this.search.addEventListener(
        'input',
        debounce((event) => {
          this.filterCountries();
        }, 200).bind(this)
      );
    }
    this.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));

  }
  filterCountries(){
    const searchValue = this.search.value.toLowerCase();
    const allCountries = this.closest("hdt-popover").querySelectorAll('hdt-richlist button');
    let visibleCountries = allCountries.length;
    allCountries.forEach((item) => {
      const countryName = item.getAttribute('data-name').toLowerCase();
      if (countryName.indexOf(searchValue) > -1) {
        item.classList.remove('hdt-d-none');
        visibleCountries++;
      } else {
        item.classList.add('hdt-d-none');
        visibleCountries--;
      }
    });
    this.closest("hdt-popover").updatePos();
    this.closest("hdt-popover").shadowRoot.querySelector('.hdt-current-scrollbar').scrollTop = 0;
  }

  onSearchKeyDown(event) {
    if (event.code.toUpperCase() === 'ENTER') {
      event.preventDefault();
    }
  }
}

customElements.define('country-filter', countryFilter);


// check purchase code
if(Shopify.designMode){

  /**
  * If it's a global variable then window[variableName] or in your case window["onlyVideo"] should do the trick.
  * https://stackoverflow.com/questions/5613834/convert-string-to-variable-name-in-javascript
  */
  function isStorageSupported(type) {
    // Return false if we are in an iframe without access to sessionStorage
    // window.self !== window.top

    var storage = (type === 'session') ? window.sessionStorage : window.localStorage;

    try {
      storage.setItem('t4s', 'test');
      storage.removeItem('t4s');
      return true;
    } catch (error) {
      return false;
    }
  };

  var ThemeCode    = atob(window[atob('Y0hWeVkyaGg=')]),
      ThemeName_base64 = window[atob('VkdobGJXVk9ZVzFsVkRR')],
      ThemeName            = atob(ThemeName_base64),
      CookieName           = 'SXNBY3RpdmVUaGVtZQ=='+ThemeName_base64,
      ShopEmail            = atob(window[atob('VTJodmNFMWxiMVEw')]),
      isTrueSet            = (sessionStorage.getItem(CookieName) === 'true' ),
      str_temp_active      = atob('I3Q0cy10ZW1wLWtleS1hY3RpdmU='), // #t4s-temp-key-active
      str_purchase         = atob('cHVyY2hhc2VfY29kZXQ0'); // purchase_codet4;

      // console.log(ThemeCode,ThemeName,ShopEmail,CookieName,str_temp_active,str_purchase)
  function alert_active_html() {
    return `<section id="${str_purchase}" style="display: flex !important">${ $4(str_temp_active).innerHTML }</section>`;
  };

  // console.log('ThemeCode', ThemeCode, isTrueSet)

  if (ThemeCode == '') {
    let dom1 = (new DOMParser).parseFromString(alert_active_html(), "text/html");
    document.body.append(dom1.body.firstElementChild);
    let dom2 = (new DOMParser).parseFromString('<div id="luffyabc194"><style>body>*:not(#purchase_codet4) {opacity: 0;pointer-events: none;</style></div>', "text/html");
    document.body.prepend(dom2.body.firstElementChild);
    sessionStorage.removeItem(CookieName);
    localStorage.removeItem(CookieName);
  }
  else if ( !isTrueSet ) {

    //console.log(ShopEmail, ThemeName, ThemeCode);

    var domain     = window.location.hostname,
    mix        = ['4','t','h','e','p','l','i','c','o','/','.',':','n','s'],
    mix_domain = mix[2]+mix[1]+mix[1]+mix[4]+mix[13]+mix[11]+mix[9]+mix[9]+mix[5]+mix[6]+mix[7]+mix[10]+mix[1]+mix[2]+mix[3]+mix[0]+mix[10]+mix[7]+mix[8]+mix[9]+mix[5]+mix[6]+mix[7]+mix[3]+mix[12]+mix[13]+mix[3]+mix[9]+mix[7]+mix[2]+mix[3]+mix[7]+'k',
    data       = {
      "shopify_domain": domain,
      "email"         : ShopEmail,
      "theme"         : ThemeName,
      "purchase_code" : ThemeCode
    };

    fetch(mix_domain, {
      "headers": {
        "accept": "*/*",
        "cache-control": "no-cache",
        "x-requested-with": "XMLHttpRequest"
      },
      "body": btoa (encodeURIComponent(JSON.stringify(data))) ,
      "method": "POST",
      "mode": "cors"
    })
    .then(function(response) {
      if(response.ok){
        return response.json()
      } throw ""
    })
    .then(function(response) {
      let dom = (new DOMParser).parseFromString(alert_active_html(), "text/html");

      if ( response.status == 1) {

        dom.body.firstElementChild.innerHTML = "<p>ACTIVATED SUCCESSFULLY. Thanks for buying my theme!</p>";
        document.body.append(dom.body.firstElementChild);

        // Set a cookie to expire in 1 hour in Javascript
        var isActived = localStorage.getItem(CookieName);
        sessionStorage.setItem(CookieName, 'true')

        if (isActived === 'true') {
          $4(atob('I3B1cmNoYXNlX2NvZGV0NA==')).remove(); // #purchase_codet4
          // $4(atob('I2x1ZmZ5YWJjMTk0'))?.remove(); //#luffyabc194
        }
        else {
          localStorage.setItem(CookieName, "true");
          setTimeout(function(){
            $4(atob('I3B1cmNoYXNlX2NvZGV0NA==')).remove(); // #purchase_codet4
            // $4(atob('I2x1ZmZ5YWJjMTk0'))?.remove(); //#luffyabc194
          }, 1000);
        }

      }
      else {

        var mess = response.message;
        if (mess == "No sale belonging to the current user found with that code") {

          dom.body.firstElementChild.innerHTML = "<p>Purchase code error. It is a sales reversal or a refund. :(((</p>";

        }
        else if (mess.length == 58 || mess.length == 101) {
          dom.body.firstElementChild.innerHTML = "<p>That license key doesn't appear to be valid. Please check your purchase code again!<br>Please open a ticket at <a href='https://support.the4.co' target='_blank'><span>support.the4.co</span></a> if you have any question.</p>";

        }
        else if (mess.length == 104) {
          dom.body.firstElementChild.innerHTML = "<p>The license not match with current theme.!<br>Please open a ticket at <a href='https://support.the4.co' target='_blank'><span>support.the4.co</span></a> if you have any question.</p>";
        }
        else {
          try {
            var mess = mess.split('active domain `')[1].split('`. ')[0];
          }
          catch(err) {
            //var mess = mess;
          }
          dom.body.firstElementChild.innerHTML = "<p>Your purchase code is invalided since it is being activated at another store "+mess+".<br> Please open a ticket at <a class='cg' href='https://support.the4.co' target='_blank'><span>support.the4.co</span></a> to get quick assistance.</p>";
        }
        document.body.append(dom.body.firstElementChild);

      }

    }).catch(function(e) {
    //}).catch((e)=>{
      console.error(e)
    });

  }
}
// end check purchase code

// readmore collection description
class DescriptionShort extends HTMLElement{
  constructor(){
    super();
    this.button_readmore = $4('[data-read-more]', this);
    this.button_readless = $4('[data-read-less]', this);
    this.short_des = $4('.hdt-collection-description-short', this);
    this.full_des = $4('.hdt-collection-description-full', this);
    if(!this.button_readmore || !this.button_readless || !this.short_des || !this.full_des){
      return;
    }
    this.button_readmore.addEventListener('click',(e)=>{
      // console.log(this);
      e.preventDefault();
      this.short_des.classList.add('hdt-hidden');
      this.full_des.classList.remove('hdt-hidden');
    })
    this.button_readless.addEventListener('click',(e)=>{
      // console.log(this);
      e.preventDefault();
      this.short_des.classList.remove('hdt-hidden');
      this.full_des.classList.add('hdt-hidden');
    })
  }
}
customElements.define('description-short',DescriptionShort)



// ===========================
//        QUICK SEARCH
// ===========================
class QuickSearch extends HTMLElement{
  constructor(){
    super();
    this.inpt = this.closest('hdt-predictive-search').querySelector('form input[data-input-search]');
    this.query = this.querySelector('a');
    if(!this.inpt || !this.query) return;
    this.onClick();
  }
  onClick(){
    let self = this;
    this.query.addEventListener('click',(e) => {
      e.preventDefault();
      self.inpt.value = e.target.dataset.query;
      self.inpt.dispatchEvent(new Event('input'));
    })
  }
}
customElements.define('hdt-quick-search', QuickSearch);


// tabHover
class tabHover extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("mouseenter", (event) => {
      this.closest("hdt-tab").querySelector("button[aria-current=\"true\"]")?.setAttribute("aria-current", false);
      this.parentElement.click()
    });
  }
}
customElements.define("tab-hover", tabHover);


// footer accodion
function handleAccordionClick() {
  let accordion = this.parentElement;
  accordion.classList.toggle('open');
  if (window.innerWidth < 768) {
    let content = accordion.querySelector('.hdt-collapse-content');
    if(!content) return;
    if (accordion.classList.contains('open')) {
      content.style.height = content.scrollHeight + 'px';
    } else {
      content.style.height = '0';
    }
  }
}
function updateMaxHeight() {
  if (window.innerWidth >= 768) {
    document.querySelectorAll('.hdt-footer-section .hdt-collapse-content').forEach(function(content) {
      content.style.height = '';
    });
  }
}
document.querySelectorAll('.hdt-footer-section .hdt-heading-f').forEach(function(element) {
  element.addEventListener('click', handleAccordionClick);
});
window.addEventListener('resize', function() {
  if (window.innerWidth < 768) {
    document.querySelectorAll('.hdt-footer-section .hdt-heading-f').forEach(function(element) {
      element.removeEventListener('click', handleAccordionClick);
      element.addEventListener('click', handleAccordionClick);
    });
  } else {
    document.querySelectorAll('.hdt-footer-section .hdt-heading-f').forEach(function(element) {
      element.removeEventListener('click', handleAccordionClick);
    });
    updateMaxHeight();
  }
});


// --------------------------
// Video popup
// --------------------------
class videoPopup extends HTMLElement {
  constructor(){
    super();
    this.dialog = $4("dialog", this);
    this.video = $4("hdt-video-player", this);
    this.input = $4("input", this);
  }
  connectedCallback(){
    this.dialog.addEventListener(`${dialogOpen}`, (e)=>{
      this.video.play();
    })
    this.dialog.addEventListener(`${dialogClose}`, (e)=>{
      this.video.pause();
    })

    if(this.input){
      this.input.addEventListener("change", (e)=>{
        if(this.input.checked){
          this.video.play();
        }else{
          this.video.pause();
        }
      })
    }
  }
}
customElements.define('hdt-video-popup', videoPopup)


// --------------------------
// Product Card Media
// --------------------------
class productCardMedia extends HTMLElement {
  constructor() {
    super();
    this.mainMedia = $4("hdt-video-player[main-media]", this);
    this.secondMedia = $4("hdt-video-player[second-media]", this);
  }
  connectedCallback(){
    this.setupEventListeners();
  }
  setupEventListeners(){
    this.addEventListener('mouseenter', () => {
      if(this.mainMedia && this.secondMedia){
        this.mainMedia.pause();
      }
      if(this.secondMedia){
        this.secondMedia.play();
      }
    })
    this.addEventListener('mouseleave', () => {
      if(this.mainMedia){
        this.mainMedia.play();
      }
      if(this.secondMedia){
        this.secondMedia.pause();
      }
    })
  }

}
customElements.define('hdt-product-card-media', productCardMedia)
